import _ from 'lodash';
import { LazyQueryResultTuple, QueryResult } from '@apollo/client/react';
import { ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'reflexbox';

import { Empty, Infinity, Paragraph, Reorder, Skeleton, Switch } from '@treams/treams-ui';
import { TargetIcon } from '@treams/treams-icons';

import * as graphql from 'generated/graphql';

import GoalCard from './GoalCard';
import GoalTree from './GoalTree';

type GoalsQuery =
	| graphql.IndividualGoalsQuery
	| graphql.GroupGoalsQuery
	| graphql.WorkspaceGoalsQuery;

function ReorderableGoalsList(props: {
	goals: graphql.Goal[];
	user?: graphql.User;
	selectedGoal?: graphql.Goal;
	onSelect?: (goal: graphql.Goal) => void;
}) {
	const [update] = graphql.useReorderGoalMutation();
	const [orders, setOrders] = useState<Record<string, number>>({});
	const goals = _.sortBy(props.goals, (goal) => -(orders[goal.id] ?? goal.order));

	function order(index: number) {
		const goal = goals[index];

		if (!goal) {
			return Date.now();
		}

		return orders[goal.id] ?? goal.order;
	}

	return (
		<Reorder.Container
			onReorder={async (id, index) => {
				const current = goals.findIndex((goal) => goal.id === id);
				const shift = current > index ? -1 : 0;

				const previous = order(index + shift);
				const next = order(index + shift + 1);
				const mean =
					index === goals.length - 1 ? order(index + shift) / 2 : (next - previous) / 2 + previous;

				setOrders({ ...orders, [id]: mean });

				await update({ variables: { input: { goalID: id, order: mean } } });

				setOrders(_.omit(orders, id));
			}}
		>
			{goals?.map((goal, index) => (
				<GoalTree.Reorder
					key={goal.id}
					index={index}
					loading={goal.id in orders}
					goal={goal}
					user={props.user}
					mb={3}
				>
					<GoalCard
						goal={goal}
						user={props.user}
						selected={props.selectedGoal?.id === goal.id}
						onSelect={props.onSelect ? () => props.onSelect?.(goal) : undefined}
						owner={false}
					/>
				</GoalTree.Reorder>
			))}
		</Reorder.Container>
	);
}

function PlainGoalsList(props: {
	goals: graphql.Goal[];
	user?: graphql.User;
	selectedGoal?: graphql.Goal;
	onSelect?: (goal: graphql.Goal) => void;
}) {
	const goals = _.sortBy(props.goals, (goal) => -goal.order);

	return (
		<>
			{goals.map((goal) => (
				<GoalTree key={goal.id} goal={goal} user={props.user} mb={3}>
					<GoalCard
						goal={goal as graphql.Goal}
						user={props.user}
						selected={props.selectedGoal?.id === goal.id}
						onSelect={props.onSelect ? () => props.onSelect?.(goal) : undefined}
						owner={false}
					/>
				</GoalTree>
			))}
		</>
	);
}

export function GenericGoalsList(props: {
	user?: graphql.User;
	query: QueryResult<GoalsQuery>;
	path: string;
	sortKey?: string;
	reorderable?: boolean;
	empty: {
		title: string;
		description: string;
	};
	selectedGoal?: graphql.Goal;
	onSelect?: (goal: graphql.Goal) => void;
}) {
	const goals = props.query.data
		? _.get(props.query.data, props.path).edges.map(({ node }) => node as graphql.Goal)
		: [];
	const sortedGoals = props.sortKey ? _.sortBy(goals, props.sortKey) : goals;

	return props.query.data ? (
		goals.length > 0 ? (
			<>
				{props.reorderable ? (
					<ReorderableGoalsList
						goals={sortedGoals}
						user={props.user}
						selectedGoal={props.selectedGoal}
						onSelect={props.onSelect}
					/>
				) : (
					<PlainGoalsList
						goals={sortedGoals}
						user={props.user}
						selectedGoal={props.selectedGoal}
						onSelect={props.onSelect}
					/>
				)}
				<Infinity query={props.query} path={props.path} reverse={!!props.query.variables?.last}>
					<Skeleton.Repeat count={3}>
						<Box mb={3}>
							<GoalCard.Skeleton />
						</Box>
					</Skeleton.Repeat>
				</Infinity>
			</>
		) : (
			<Skeleton.Empty
				contents={
					<Empty
						icon={<TargetIcon />}
						title={props.empty.title}
						description={props.empty.description}
					/>
				}
			>
				<Skeleton.Repeat count={3}>
					<Box mb={3}>
						<GoalCard.Skeleton />
					</Box>
				</Skeleton.Repeat>
			</Skeleton.Empty>
		)
	) : (
		<GenericGoalsList.Skeleton />
	);
}

GenericGoalsList.Skeleton = function GenericGoalsListSkeleton() {
	return (
		<Skeleton.Repeat count={3}>
			<Box mb={3}>
				<GoalCard.Skeleton />
			</Box>
		</Skeleton.Repeat>
	);
};

export default function GoalsList(props: {
	user?: graphql.User;
	query: QueryResult<GoalsQuery>;
	path: string;
	moments?: ReactNode;
	completedQuery?: QueryResult<GoalsQuery>;
	archivedQuery?: LazyQueryResultTuple<GoalsQuery, object>;
	selectedGoal?: graphql.Goal;
	onSelect?: (goal: graphql.Goal) => void;
	reorderable?: boolean;
}) {
	const [showArchived, setShowArchived] = useState<boolean>(false);
	const { t } = useTranslation();

	useEffect(() => {
		if (!showArchived) {
			return;
		}

		props.archivedQuery?.[0]();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [showArchived]);

	const hasCompletedGoals = props.completedQuery?.data
		? _.get(props.completedQuery.data, props.path).edges.length > 0
		: false;

	return (
		<>
			{props.archivedQuery && (
				<Box mb={4}>
					<Switch
						label={t('goals.list.archivedGoals.button')}
						on={showArchived}
						onChange={(on) => setShowArchived(on)}
					/>
				</Box>
			)}

			{!props.onSelect && props.moments}

			{!showArchived ? (
				<>
					<Box mb={4}>
						<Paragraph mb={3}>{t('goals.activeGoals.title')}</Paragraph>
						<GenericGoalsList
							user={props.user}
							query={props.query}
							path={props.path}
							reorderable={props.reorderable}
							empty={{
								title: t('goals.index.empty.title'),
								description: t('goals.index.empty.description'),
							}}
							selectedGoal={props.selectedGoal}
							onSelect={props.onSelect}
						/>
					</Box>

					{hasCompletedGoals && (
						<Box>
							<Paragraph mb={3}>{t('goals.completedGoals.title')}</Paragraph>
							<GenericGoalsList
								user={props.user}
								query={props.completedQuery!}
								path={props.path}
								reorderable={props.reorderable}
								empty={{
									title: t('goals.completed.empty.title'),
									description: t('goals.completed.empty.description'),
								}}
								selectedGoal={props.selectedGoal}
								onSelect={props.onSelect}
							/>
						</Box>
					)}
				</>
			) : (
				<Box>
					<Paragraph mb={3}>{t('goals.archivedGoals.title')}</Paragraph>
					<GenericGoalsList
						user={props.user}
						query={props.archivedQuery![1]}
						path={props.path}
						sortKey="archivedAt"
						reorderable={props.reorderable}
						empty={{
							title: t('goals.archive.empty.title'),
							description: t('goals.archive.empty.description'),
						}}
						selectedGoal={props.selectedGoal}
						onSelect={props.onSelect}
					/>
				</Box>
			)}
		</>
	);
}

GoalsList.Skeleton = function GoalsListSkeleton() {
	return (
		<Box mt={1}>
			<Paragraph.Skeleton mb={3} lines={1} />
			<GenericGoalsList.Skeleton />
		</Box>
	);
};
